import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ImageUploadZone = ({ onImageSelect, selectedImage, error, title, selectedAlt }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileSelection = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes?.includes(file?.type)) {
      onImageSelect(null, 'JPG, PNG, WEBP 형식만 지원됩니다');
      return;
    }

    if (file?.size > maxSize) {
      onImageSelect(null, '파일 크기는 5MB 이하여야 합니다');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      onImageSelect({ file, preview: e?.target?.result }, null);
    };
    reader?.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  const handleCameraClick = () => {
    if (fileInputRef?.current) {
      fileInputRef?.current?.setAttribute('capture', 'environment');
      fileInputRef?.current?.click();
    }
  };

  const handleRemoveImage = () => {
    onImageSelect(null, null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileInputChange}
        className="hidden"
      />
      {!selectedImage ? (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative w-full h-64 md:h-80 lg:h-96 border-2 border-dashed rounded-lg transition-gaming ${
            isDragging
              ? 'border-primary bg-primary/10'
              : error
              ? 'border-error bg-error/5' :'border-border bg-muted/30 hover:border-primary hover:bg-muted/50'
          }`}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 md:p-8">
            <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-primary/20 rounded-full flex items-center justify-center mb-4 md:mb-6">
              <Icon
                name="Upload"
                size={32}
                color="var(--color-primary)"
                className="md:w-10 md:h-10 lg:w-12 lg:h-12"
              />
            </div>

            <h3 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground mb-2 text-center">
              {isDragging ? '여기에 놓으세요' : (title || '증명 이미지 업로드')}
            </h3>

            <p className="text-sm md:text-base text-muted-foreground text-center mb-4 md:mb-6 caption">
              드래그 앤 드롭 또는 클릭하여 파일 선택
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-sm">
              <button
                onClick={handleBrowseClick}
                className="w-full sm:flex-1 px-4 md:px-6 py-2 md:py-3 bg-primary text-primary-foreground rounded-md font-medium transition-gaming hover:shadow-gaming-md active:translate-y-px flex items-center justify-center space-x-2"
              >
                <Icon name="FolderOpen" size={20} />
                <span>파일 선택</span>
              </button>

              <button
                onClick={handleCameraClick}
                className="w-full sm:flex-1 px-4 md:px-6 py-2 md:py-3 bg-secondary text-secondary-foreground rounded-md font-medium transition-gaming hover:shadow-gaming-md active:translate-y-px flex items-center justify-center space-x-2 lg:hidden"
              >
                <Icon name="Camera" size={20} />
                <span>카메라</span>
              </button>
            </div>

            <p className="text-xs md:text-sm text-muted-foreground text-center mt-4 caption">
              JPG, PNG, WEBP (최대 5MB)
            </p>
          </div>
        </div>
      ) : (
        <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden bg-muted">
          <Image
            src={selectedImage?.preview}
            alt={selectedAlt || 'Selected proof image showing uploaded content for challenge verification'}
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-background/20" />

          <button
            onClick={handleRemoveImage}
            className="absolute top-4 right-4 w-10 h-10 bg-error text-error-foreground rounded-full flex items-center justify-center transition-gaming active:translate-y-px shadow-gaming-md"
            aria-label="Remove image"
          >
            <Icon name="X" size={20} />
          </button>

          <div className="absolute bottom-4 left-4 right-4 flex items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-success/20 rounded-md flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {selectedImage?.file?.name}
              </p>
              <p className="text-xs text-muted-foreground caption">
                {(selectedImage?.file?.size / 1024 / 1024)?.toFixed(2)} MB
              </p>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-center space-x-2 mt-3 text-error">
          <Icon name="AlertCircle" size={16} />
          <span className="text-sm caption">{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUploadZone;